import React, {
  CSSProperties,
  FC,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import {
  Combobox,
  makeStyles,
  Option,
  useId,
  Spinner,
} from "@fluentui/react-components";
import type {
  ComboboxProps,
  OptionOnSelectData,
  ComboboxOpenChangeData,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: "2px",
    maxWidth: "max-content",
    minWidth: "min-content",
  },
  combobox: {
    minWidth: "200px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "40px",
  },
});

interface OptionType {
  label: string;
  value: string;
}

interface CustomDropDownProps extends Partial<ComboboxProps> {
  label?: string;
  optionsFetcher?: () => Promise<OptionType[]>;
  values?: OptionType[];
  selectedValues?: string[]; // ✅ NEW (controlled)
  onSelectionChange?: (selectedValues: string[]) => void;
  className?: string;
  style?: CSSProperties;
  multiSelect?: boolean;
}

const ASCDropDown: FC<CustomDropDownProps> = ({
  label,
  optionsFetcher,
  values,
  selectedValues: controlledSelectedValues,
  onSelectionChange,
  className,
  style,
  multiSelect = false,
  ...rest
}) => {
  const comboId = useId("combo-default");
  const styles = useStyles();

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [internalSelected, setInternalSelected] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");

  const activeOptions = optionsFetcher ? options : values || [];

  /* -------------------------------------------------
     Sync controlled values → internal state
  -------------------------------------------------- */
  useEffect(() => {
    if (Array.isArray(controlledSelectedValues)) {
      setInternalSelected(controlledSelectedValues);
    }
  }, [controlledSelectedValues]);

  /* -------------------------------------------------
     Sync selected labels to Combobox input value
  -------------------------------------------------- */
  useEffect(() => {
    const selectedLabels = activeOptions
      .filter((o) => internalSelected.includes(o.value))
      .map((o) => o.label);
    setInputValue(selectedLabels.join(", "));
  }, [internalSelected, activeOptions]);

  const handleOptionSelect = (
    _: SyntheticEvent<HTMLElement>,
    data: OptionOnSelectData,
  ) => {
    const clickedValue = data.optionValue;
    if (!clickedValue) return;

    const updated = internalSelected.includes(clickedValue)
      ? internalSelected.filter((v) => v !== clickedValue)
      : multiSelect
      ? [...internalSelected, clickedValue]
      : [clickedValue];

    setInternalSelected(updated);
    onSelectionChange?.(updated);
  };

  const handleOpenChange = async (
    _: SyntheticEvent<HTMLElement>,
    data: ComboboxOpenChangeData,
  ) => {
    if (data.open && optionsFetcher && options.length === 0) {
      setLoading(true);
      try {
        setOptions(await optionsFetcher());
      } catch (err) {
        console.error("Failed to fetch options", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const onInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value);
  };

  return (
    <div className={`${styles.root} ${className || ""}`} style={style}>
      {label && <label id={comboId}>{label}</label>}

      <Combobox
        aria-labelledby={comboId}
        multiselect={multiSelect}
        className={styles.combobox}
        onOptionSelect={handleOptionSelect}
        onOpenChange={handleOpenChange}
        selectedOptions={internalSelected}
        value={inputValue}
        onChange={onInput}
        {...rest}
      >
        {loading ? (
          <div className={styles.loaderContainer}>
            <Spinner size="medium" />
          </div>
        ) : (
          activeOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))
        )}
      </Combobox>
    </div>
  );
};

export default ASCDropDown;
